import type { Appointment, WorkOrder, WorkPhase } from "@myapp/contract";
import { deriveWorkPhase } from "@myapp/contract";
import {
  getAppointmentStatusColor,
  getAppointmentStatusLabel,
  isAppointmentClosed,
  useAppointmentDetail,
  useAppointmentsByWorkOrder,
  useRevertAppointmentPhase,
  useStartAppointment,
  useStartAppointmentTravel,
} from "../../../entities/appointment";
import { useAuthStore } from "../../../features/auth";
import { getPrimaryWorkAction, usePhaseTaps } from "../../../features/work-session";
import type { PhaseTaps, PrimaryWorkAction } from "../../../features/work-session";
import { isSameDay } from "../../../shared/helpers/date";
import { buildTitle } from "../lib";

export interface WorkerAppointmentVm {
  appointment: Appointment;
  ext: WorkOrder["external"] | undefined;
  title: string;
  statusLabel: string;
  statusColor: string;
  mapsQuery: string | null | undefined;
  phase: WorkPhase | null;
  primaryAction: PrimaryWorkAction | null;
  showActionButtons: boolean;
  elapsedSince: string | null;
  previousAppointments: Appointment[];
  technicianId: number;
}

export interface UseWorkerAppointmentResult {
  isLoading: boolean;
  isError: boolean;
  taps: PhaseTaps;
  /** null while loading or on error — narrow on this, not on isLoading/isError, before reading it. */
  vm: WorkerAppointmentVm | null;
}

/**
 * Data + derived view-model for the worker appointment screen: the detail query, its previous
 * appointments in the same series, the phase-tap mutations, and every value the desktop/mobile
 * views compute from them. `taps` is created unconditionally — the caller's hooks (and this one's)
 * must all run above any early return, since `data` is still undefined on the first renders.
 */
export function useWorkerAppointment(appointmentId: number | undefined): UseWorkerAppointmentResult {
  const { user } = useAuthStore();
  const { data, isLoading, isError } = useAppointmentDetail(appointmentId);
  const isFollowUp = data && data.sequenceNumber > 1;
  const { data: siblingAppointments } = useAppointmentsByWorkOrder(
    isFollowUp ? data.workOrderId : null,
  );

  const startTravel = useStartAppointmentTravel();
  const startWork = useStartAppointment();
  const revertPhase = useRevertAppointmentPhase();
  const taps = usePhaseTaps({
    'start-travel': (body) =>
      startTravel.mutateAsync({ id: appointmentId!, workOrderId: data?.workOrderId, body }),
    start: (body) => startWork.mutateAsync({ id: appointmentId!, workOrderId: data?.workOrderId, body }),
    undo: () => revertPhase.mutateAsync({ id: appointmentId!, workOrderId: data?.workOrderId }),
  });

  if (!data) {
    return { isLoading, isError, taps, vm: null };
  }

  const ext = data.workOrder?.external;
  // Hauseingang is the actual entrance the technician drives to; gebaeude names the
  // Wirtschaftseinheit and is only a fallback for orders without a linked Hauseingang.
  const mapsQuery = ext?.hauseingang ?? ext?.gebaeude;
  const phase = deriveWorkPhase(data.schedule);
  // A worker only acts on the appointment on its own day — "Umplanen" and the fresh start stay
  // hidden otherwise. An open phase keeps its next action so a session survives past midnight,
  // including an open Abfahrt on an already-completed appointment.
  const isToday = isSameDay(data.schedule.plannedWorkStartsAt, new Date());
  const primaryAction = getPrimaryWorkAction({
    isCompleted: isAppointmentClosed(data.status),
    isScheduled: true,
    isToday,
    phase,
    plannedTravelMinutes: data.schedule.plannedTravelMinutes,
  });
  /** ISO anchor of the running phase — feeds the elapsed chip on the primary button. */
  const elapsedSince =
    phase === 'ANFAHRT'
      ? data.schedule.travelStartedAt
      : phase === 'ARBEIT'
        ? data.schedule.actualWorkStartsAt
        : phase === 'ABFAHRT'
          ? data.schedule.actualEndsAt
          : null;

  const vm: WorkerAppointmentVm = {
    appointment: data,
    ext,
    title: buildTitle(ext, data.workOrderId),
    statusLabel: getAppointmentStatusLabel(data.status, data),
    statusColor: getAppointmentStatusColor(data.status, data),
    mapsQuery,
    phase,
    primaryAction,
    showActionButtons: primaryAction !== null,
    elapsedSince,
    previousAppointments: (siblingAppointments ?? []).filter((a) => a.id !== appointmentId),
    technicianId: Number(user?.id ?? 0),
  };

  return { isLoading, isError, taps, vm };
}
